const isProd = process.env.REDUX_ENV === 'production'

import React, { Component, Fragment } from 'react'
import styled from 'styled-components'
import { Formik, Field } from 'formik'
import { debounce } from 'lodash'

// styles
import { ModalHeader, ScreenWrapper, ModalContainer, CloseIcon } from './styles'

// utils and config
import th from 'utils/themeHelper'
import constants from 'config/constants'
import l from 'config/localization'
import goalTypeOutcomeBuilder from 'utils/goalTypeOutcomeBuilder'

// Load components synchronously
import CheckboxInput from 'components/CheckboxInput'
import ConditionalDisplay from 'components/ConditionalDisplay/ConditionalDisplay'
import FieldRow from 'components/FieldRow/FieldRow'
import FileUpload from 'components/FileUpload/FileUpload'
import NumberInput from 'components/NumberInput/NumberInput'
import PlacesContainer from 'components/PlacesContainer'
import PaymentOptionSelector from 'components/PaymentOptionSelector/PaymentOptionSelector'
import RadioGroup from 'components/RadioGroup/RadioGroup'
import SearchInput from 'components/SearchInput/SearchInput'
import SmallButtonPrimary from 'components/SmallButtonPrimary/SmallButtonPrimary'
import StaticSelect from 'components/Select/StaticSelect'
import SwipeSelect from 'components/Select/SwipeSelect'
import TextArea from 'components/TextArea/TextArea'
import TextInput from 'components/TextInput/TextInput'
import { Error, Label } from 'components/InputLabels/InputLabels'

// assets
import { ReactComponent as privateIcon } from 'assets/images/svgs/icon-public-opp.svg'
import { ReactComponent as publicIcon } from 'assets/images/svgs/icon-private-opp.svg'
import { ReactComponent as DonationIcon } from 'assets/images/cause-type-icons/money_icon.svg'
import { ReactComponent as GoodsIcon } from 'assets/images/cause-type-icons/goods_icon.svg'

import { Settings } from 'styled-icons/ionicons-outline'

const FieldRowInner = styled.div`
  display: flex;
`

const FormDividerLine = styled.div`
  background: ${th('borders.default')};
  display: block;
  margin: 30px 0;
  padding: 1px 0;
  width: 100%;
`

const NewFieldRow = styled.div`
  display: block;
  margin-bottom: ${props => props.marginBottom || '15px'};
  margin-top: ${props => props.marginTop || '15px'};
`

const SubLabel = styled.span`
  font-size: 12px;
  color: ${th('text.light')};
  padding: 0;
  position: relative;
  margin: -2px 2px 6px;
  font-family: ${th('fonts.light')};
  line-height: 1.25em;
`

const ExpandIcon = styled.div`
  display: inline-block;
  margin-right: 8px;
  margin-top: 2px;
  vertical-align: top;

  svg {
    border: 1px solid ${th('borders.default')};
    border-radius: 4px;
    height: 30px;
    padding: 2px;
    width: 30px;

    &:hover {
      border: 1px solid ${th('borders.dark')};
      cursor: pointer;
    }
  }
`

const TermsContainer = styled.div`
  background: white;
  height: 200px;
  overflow: scroll;
  padding: 15px;
  border-radius: 4px;
  border: 1px solid ${th('borders.default')};
  margin-bottom: 10px;
`

const storyStatusOpts = [
  {
    label: 'Visible',
    value: constants.STORY_STATES.PUBLIC,
    iconPadding: '5px',
    Icon: publicIcon
  },
  {
    label: 'Anonymous',
    value: constants.STORY_STATES.ANON_DONATION,
    iconPadding: '5px',
    Icon: privateIcon
  }
]

const TermsLabel = () => <Fragment>I accept the additional terms & conditions</Fragment>

const validate = values => {
  const { customSignupQuestions = [], customTerms, isMoneyType, isMoneyOrGoodGivingType } = values
  const errors = {}

  customSignupQuestions.forEach(q => {
    const isDoc = [
      constants.ADDITIONAL_QUESTION_TYPES.document,
      constants.ADDITIONAL_QUESTION_TYPES.Document
    ].includes(q.request_type)
    const isQuestion = !isDoc

    const answerOrDoc = values[`custom_signup_question_${q.id}`]
    const answerIsZero = typeof answerOrDoc === 'number' && '0' === `${answerOrDoc}`
    const answerExists = answerOrDoc || answerIsZero
    const requiredQuestion = q.is_required || q.isRequired

    if (isDoc) {
      if (!answerOrDoc && requiredQuestion) {
        errors[`custom_signup_question_${q.id}`] = 'Please attach a PDF of no more than 3mb'
      } else if (answerOrDoc && answerOrDoc.size / 1024 / 1024 > 3) {
        errors[`custom_signup_question_${q.id}`] = 'PDF size is more than 3mb'
      }
    } else if (isQuestion && requiredQuestion && !answerExists) {
      errors[`custom_signup_question_${q.id}`] = 'Please answer this question'
    }
  })

  if (customTerms && !values.extra_terms) {
    errors.extra_terms = 'Please accept the additional terms first'
  }

  if (isMoneyOrGoodGivingType && !values.applicant_commitment) {
    errors.applicant_commitment = 'Please include a number you would commit to'
  }

  if (isMoneyType && !values.chosen_donation_link) {
    errors.chosen_donation_link = 'Please select how you would like to donate'
  }

  return errors
}

const DonationContextSection = ({ isSuperAdmin, orgOpts, setState, showContextField }) => {
  if (!isSuperAdmin) return null

  if (!showContextField) {
    return (
      <NewFieldRow marginTop="20px">
        <FieldRowInner>
          <ExpandIcon>
            <Settings onClick={() => setState({ showContextField: true })} />
          </ExpandIcon>

          <div style={{ display: 'inline-block' }}>
            <Label>You're making a personal donation</Label>
            <SubLabel>
              To donate on behalf of one of your {l('ORGS')}, click the settings icon.
            </SubLabel>
          </div>
        </FieldRowInner>
      </NewFieldRow>
    )
  }

  return (
    <NewFieldRow>
      <Field
        name="chosen_donation_context"
        placeholder="Select an option"
        component={StaticSelect}
        label="Select a donation context"
        subLabel="You can either donate in a personal capacity or you can make a donation on behalf of any of your organisations"
        options={orgOpts}
      />
    </NewFieldRow>
  )
}

const StoryVisibilitySection = ({
  formValues,
  setFieldValue,
  setState,
  showStoryVisibilityField
}) => {
  if (!showStoryVisibilityField) {
    return (
      <NewFieldRow marginBottom="20px">
        <FieldRowInner>
          <ExpandIcon>
            <Settings onClick={() => setState({ showStoryVisibilityField: true })} />
          </ExpandIcon>

          <div style={{ display: 'inline-block' }}>
            <Label>Your donation will be visible</Label>
            <SubLabel>
              To manage the privacy and visibility of your donation, click the settings icon.
            </SubLabel>
          </div>
        </FieldRowInner>
      </NewFieldRow>
    )
  }

  return (
    <Fragment>
      <NewFieldRow>
        <Field
          component={SwipeSelect}
          forceFullWidth
          forceShowOptions
          label="Story visibility"
          name="story_privacy"
          onChange={e => {
            if (e) {
              setFieldValue(`story_privacy`, e.value)
            }
          }}
          options={storyStatusOpts}
          shortOptions
          subLabel={
            'Donations made via Brownie Points are shared in various places on the platform. If you prefere to donate anonymously, you can choose to keep your name private. Private stories are also hidden from others on your user profile.'
          }
          value={formValues.story_privacy}
        />
      </NewFieldRow>
    </Fragment>
  )
}

class ExtraInfoModal extends Component {
  constructor(props) {
    super(props)
    this.state = {
      showContextField: false,
      showCustomAmountField: false,
      showStoryVisibilityField: false
    }
    this.debounceFetchPlaces = debounce(this.loadPlaces, 400)
  }

  loadPlaces(value, fetchPlaces) {
    const { placesQuery } = this.props

    if (value !== placesQuery) {
      return fetchPlaces(value)
    }
  }

  render() {
    const { showCustomAmountField, showContextField, showStoryVisibilityField } = this.state

    const {
      extraInfoModalOpts = {},
      hideModal,
      fetchPlaces,
      locationIsLoading,
      places = []
    } = this.props

    const {
      formValues,
      isSubmitting,
      opportunity = {},
      organisation = {},
      orgOpts = [],
      roleData,
      userData = {}
    } = extraInfoModalOpts

    const goalType = opportunity.goal_type || constants.OPPORTUNITY_GOAL_TYPES.MONEY
    const questions = opportunity.customSignupQuestions || []
    const displayableOutcome = constants.OPPORTUNITY_GOAL_TYPE_DELIVERABLES[goalType]
    const donationOpts = opportunity.donationOpts || organisation.donationOpts || []
    const hasDonoOpts = donationOpts.length > 0
    const isMoneyTypeWithoutDonationOpts = formValues.isMoneyType && !hasDonoOpts
    const isSuperAdmin = roleData.some(r => r.role_type === constants.ROLES.SUPERADMIN)
    const isMoneyType =
      formValues.isMoneyType || goalType === constants.OPPORTUNITY_GOAL_TYPES.MONEY

    const deliverable = constants.OPPORTUNITY_GOAL_TYPE_DELIVERABLES[goalType]
    const singleDeliverable = deliverable.replace(/s$/, '')
    const autoSelectDonationOption = isMoneyType && hasDonoOpts
    const chosenDonationLink = autoSelectDonationOption ? donationOpts[0].id : undefined

    const commitmentSubLabel = isMoneyType
      ? `Amount in ${constants.OPPORTUNITY_GOAL_TYPE_DELIVERABLES[goalType]}`
      : `Amount of ${constants.OPPORTUNITY_GOAL_TYPE_DELIVERABLES[goalType]}`

    const commitmentOpts = isMoneyType
      ? [
          { label: 'R50', value: 50, iconPadding: '5px', Icon: DonationIcon },
          { label: 'R100', value: 100, iconPadding: '5px', Icon: DonationIcon },
          { label: 'R250', value: 250, iconPadding: '5px', Icon: DonationIcon },
          { label: 'R500', value: 500, iconPadding: '5px', Icon: DonationIcon },
          { label: 'R1,000', value: 1000, iconPadding: '5px', Icon: DonationIcon },
          { label: 'Other', value: 'other', iconPadding: '5px', Icon: DonationIcon }
        ]
      : [
          {
            label: `1 ${singleDeliverable}`,
            value: 1,
            iconPadding: '5px',
            Icon: GoodsIcon
          },
          {
            label: `2 ${deliverable}`,
            value: 2,
            iconPadding: '5px',
            Icon: GoodsIcon
          },
          {
            label: `5 ${deliverable}`,
            value: 5,
            iconPadding: '5px',
            Icon: GoodsIcon
          },
          {
            label: `10 ${deliverable}`,
            value: 10,
            iconPadding: '5px',
            Icon: GoodsIcon
          },
          { label: 'Other', value: 'other', iconPadding: '5px', Icon: GoodsIcon }
        ]

    if (isMoneyTypeWithoutDonationOpts) {
      return (
        <ScreenWrapper>
          <ModalContainer>
            <CloseIcon dark size={30} onClick={hideModal} />

            <ModalHeader>Whoops! There seems to be a problem.</ModalHeader>
            <p>
              The beneficiary of this donation {l('OPP')} does not seem to have any valid donation
              options available. Please let them know, and once they add or enable one or more
              options, you'll be able to retry your donation.
            </p>
          </ModalContainer>
        </ScreenWrapper>
      )
    }

    if (isSubmitting) {
      return (
        <ScreenWrapper>
          <ModalContainer>
            <CloseIcon dark size={30} onClick={hideModal} />

            <ModalHeader>Hang on a second</ModalHeader>
            <p>
              We're submitting your form. Hopefully this doesn't take longer than a few seconds...
            </p>
          </ModalContainer>
        </ScreenWrapper>
      )
    }

    return (
      <ScreenWrapper>
        <ModalContainer>
          <CloseIcon dark size={30} onClick={hideModal} />

          <ModalHeader>Thank you {userData.f_name}, you're almost there.</ModalHeader>

          <ConditionalDisplay displayWhen={[questions.length]}>
            <p>
              To join this {l('OPP')}, the host has requested more information about you. Please
              answer the questions below.
            </p>
          </ConditionalDisplay>

          <Formik
            initialValues={{
              ...formValues,
              applicant_commitment: undefined,
              chosen_donation_context: orgOpts[0].value,
              chosen_donation_link: chosenDonationLink
            }}
            enableReinitialize
            validate={validate}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={values => {
              const vals = { ...values }

              extraInfoModalOpts.signUp({ ...vals })
            }}
            render={({ errors, handleSubmit, setFieldError, setFieldValue, values }) => {
              const isPledgeType =
                values.goal_type === constants.OPPORTUNITY_GOAL_TYPES.VOLUNTEERING
              const isMoneyOrGoodGivingType =
                values.goal_type === constants.OPPORTUNITY_GOAL_TYPES.MONEY ||
                values.goal_type === constants.OPPORTUNITY_GOAL_TYPES.GOODS
              const formSubmitLabel = isMoneyOrGoodGivingType
                ? 'Donate'
                : isPledgeType
                ? 'Pledge'
                : 'Volunteer'

              return (
                <form onSubmit={handleSubmit}>
                  {questions.map((q, idx) => {
                    const requiredQuestion = q.is_required || q.isRequired
                    const isDoc = [
                      constants.ADDITIONAL_QUESTION_TYPES.document,
                      constants.ADDITIONAL_QUESTION_TYPES.Document
                    ].includes(q.request_type)
                    const isLocation =
                      q.request_type === constants.ADDITIONAL_QUESTION_TYPES.Location
                    const isSingleChoiceQuestion =
                      q.request_type === constants.ADDITIONAL_QUESTION_TYPES.SingleChoice

                    const QuestionComponent = isDoc
                      ? FileUpload
                      : q.request_type === constants.ADDITIONAL_QUESTION_TYPES.Text
                      ? TextInput
                      : q.request_type === constants.ADDITIONAL_QUESTION_TYPES.Integer
                      ? NumberInput
                      : q.request_type === constants.ADDITIONAL_QUESTION_TYPES.integer_question
                      ? NumberInput
                      : TextInput

                    if (isSingleChoiceQuestion) {
                      return (
                        <FieldRow key={q.id}>
                          <Field
                            requiredField={requiredQuestion}
                            name={`custom_signup_question_${q.id}`}
                            onChange={val => setFieldValue(`custom_signup_question_${q.id}`, val)}
                            options={q.options}
                            component={RadioGroup}
                            title={q.label}
                          />
                        </FieldRow>
                      )
                    }

                    if (isLocation) {
                      return (
                        <FieldRow key={q.id}>
                          <PlacesContainer>
                            {() => (
                              <SearchInput
                                suggestions={places}
                                field={{ name: `custom_signup_question_${q.id}` }}
                                form={{ errors: errors }}
                                name={`custom_signup_question_${q.id}`}
                                isLoading={locationIsLoading}
                                locationValue={values[`custom_signup_question_${q.id}`]}
                                locationFieldName={values[`custom_signup_question_${q.id}`]}
                                clearLocation={() =>
                                  setFieldValue(`custom_signup_question_${q.id}`, '')
                                }
                                label={q.label}
                                placeholder="Type and select an address or place"
                                value={values[`custom_signup_question_${q.id}_input`]}
                                onSuggestionsFetchRequested={({ value }) =>
                                  this.debounceFetchPlaces(value, fetchPlaces)
                                }
                                onSuggestionsClearRequested={() => null}
                                getSuggestionValue={value => value}
                                onSuggestionSelected={(_event, { suggestion }) => {
                                  setFieldValue(`custom_signup_question_${q.id}_input`, '')
                                  setFieldValue(`custom_signup_question_${q.id}`, suggestion || '')
                                  setFieldError(`custom_signup_question_${q.id}`, null)
                                }}
                                onChange={(_e, { method, newValue }) => {
                                  if (method === 'type') {
                                    setFieldValue(`custom_signup_question_${q.id}_input`, newValue)
                                  }
                                }}
                                requiredField={requiredQuestion}
                              />
                            )}
                          </PlacesContainer>
                        </FieldRow>
                      )
                    }

                    if (isDoc) {
                      return (
                        <FieldRow key={q.id}>
                          <Field
                            accept="application/pdf"
                            buttonText="Choose file"
                            component={QuestionComponent}
                            cropOpts={{ aspect: 'none' }}
                            disabled={isSubmitting}
                            index={idx}
                            label={q.label}
                            name={`custom_signup_question_${q.id}`}
                            onChange={event => {
                              setFieldValue(`custom_signup_question_${q.id}`, event.target.files[0])
                            }}
                            placeholder="Upload a document (PDF only)"
                            requiredField={requiredQuestion}
                            setFieldError={setFieldError}
                            setFieldValue={setFieldValue}
                          />
                        </FieldRow>
                      )
                    }

                    return (
                      <FieldRow key={q.id}>
                        <Field
                          name={`custom_signup_question_${q.id}`}
                          index={idx}
                          maxLength="360"
                          type={q.request_type}
                          label={q.label}
                          placeholder="Your answer"
                          component={QuestionComponent}
                          requiredField={requiredQuestion}
                        />
                      </FieldRow>
                    )
                  })}

                  <ConditionalDisplay displayWhen={[formValues.isMoneyOrGoodGivingType]}>
                    <Fragment>
                      <ConditionalDisplay displayWhen={[opportunity.id]}>
                        <FieldRow>
                          <p>
                            The goal of this {l('OPP')} is{' '}
                            {goalTypeOutcomeBuilder({
                              goalType: goalType,
                              displayableOutcome,
                              value: opportunity.outcome_target
                            })}
                            . Please include the amount you think you'd be able to commit to.
                          </p>
                        </FieldRow>
                      </ConditionalDisplay>

                      <NewFieldRow marginBottom="10px">
                        <Field
                          component={SwipeSelect}
                          forceFullWidth
                          forceShowOptions
                          label={'Select your commitment'}
                          mobileWrapped
                          name="applicant_commitment"
                          onChange={e => {
                            if (e) {
                              setFieldValue(`applicant_commitment`, e.value)
                              this.setState({ showCustomAmountField: e.value === 'other' })
                            }
                          }}
                          options={commitmentOpts}
                          requiredField
                          shortOptions
                          subLabel={commitmentSubLabel}
                        />
                      </NewFieldRow>

                      <ConditionalDisplay displayWhen={[showCustomAmountField]}>
                        <FieldRow>
                          <Field
                            component={NumberInput}
                            label={'Add a custom commitment amount'}
                            name="applicant_commitment_other"
                            max="1000000"
                            min={isProd ? '10' : '5'}
                            placeholder={`R50`}
                            requiredField
                            shortNumberField
                            type="number"
                          />
                        </FieldRow>
                      </ConditionalDisplay>

                      <ConditionalDisplay displayWhen={[isMoneyType]}>
                        <NewFieldRow marginTop="10px">
                          <Field
                            component={TextArea}
                            label={`Donation message`}
                            maxWidth="500px"
                            name="donation_message"
                            placeholder={'Optionally add a donation message'}
                          />
                        </NewFieldRow>

                        <NewFieldRow marginTop="10px">
                          <Label>Select a payment method *</Label>

                          <ConditionalDisplay displayWhen={[errors.chosen_donation_link]}>
                            <Error>Please select a payment method</Error>
                          </ConditionalDisplay>

                          <PaymentOptionSelector
                            chosenValue={values.chosen_donation_link}
                            donationOpts={donationOpts}
                            setFieldValue={setFieldValue}
                          />
                        </NewFieldRow>
                      </ConditionalDisplay>

                      <FormDividerLine />

                      <DonationContextSection
                        isSuperAdmin={isSuperAdmin}
                        orgOpts={orgOpts}
                        setState={v => this.setState(v)}
                        showContextField={showContextField}
                      />

                      <StoryVisibilitySection
                        formValues={formValues}
                        setState={v => this.setState(v)}
                        setFieldValue={setFieldValue}
                        showStoryVisibilityField={showStoryVisibilityField}
                      />
                    </Fragment>
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[opportunity.custom_terms]}>
                    <FieldRow>
                      <p>This {l('OPP')} has additional terms and conditions.</p>
                      <TermsContainer>{opportunity.custom_terms}</TermsContainer>
                      <Field name="extra_terms" label={<TermsLabel />} component={CheckboxInput} />
                    </FieldRow>
                  </ConditionalDisplay>

                  <ConditionalDisplay displayWhen={[opportunity.has_applicant_vetting]}>
                    <p>
                      The host of this {l('OPP')} has chosen to review and approve {l('SUPPORTERS')}{' '}
                      who sign up before they can join this {l('OPP')}. If your application is
                      accepted, you will receive an email confirmation. Thanks for your
                      understanding.
                    </p>
                  </ConditionalDisplay>

                  <FieldRow>
                    <SmallButtonPrimary type="submit">{formSubmitLabel}</SmallButtonPrimary>
                  </FieldRow>
                </form>
              )
            }}
          />
        </ModalContainer>
      </ScreenWrapper>
    )
  }
}

export default ExtraInfoModal
